import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  CircularProgress,
  Chip,
} from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { expensePaymentAPI, PaymentSuggestion } from "../../services/api/api";
import { Expense } from "../../lib/types";

interface LinkTransactionDialogProps {
  open: boolean;
  expense: Expense | null;
  // Returns the chosen transaction id, or null to clear the link.
  // When a transaction is chosen, its amount/date are passed along so the
  // caller can compare against the expense amount (auto/prompt mark-as-paid).
  onConfirm: (
    transactionId: string | null,
    transaction?: { amount: number; date?: string }
  ) => Promise<void> | void;
  onCancel: () => void;
}

const toDateInputValue = (date: Date): string =>
  date.toISOString().slice(0, 10);

const LinkTransactionDialog: React.FC<LinkTransactionDialogProps> = ({
  open,
  expense,
  onConfirm,
  onCancel,
}) => {
  const [suggestions, setSuggestions] = useState<PaymentSuggestion[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selected, setSelected] = useState<{
    id: string;
    label: string;
    amount: number;
    date?: string;
  } | null>(null);
  const [saving, setSaving] = useState(false);

  const expenseId = expense?.id || "";
  const alreadyLinkedId = expense?.linkedTransactionId || null;

  useEffect(() => {
    if (open) {
      // Seed the search box with the expense's display title so matching
      // transactions surface immediately instead of an empty search. The
      // suggestions effect below re-runs because it depends on searchQuery.
      // Strip common recurring-instance numeric suffixes (e.g. "Rent 11",
      // "Internet Payment 3") so the seeded query matches transaction
      // name/merchant more reliably. Kept fully editable by the user.
      const rawTitle = (expense?.name || "").trim();
      const seededTitle =
        rawTitle
          .replace(/\s+payment\s+\d+$/i, "")
          .replace(/\s+\d+$/, "")
          .trim() || rawTitle;
      console.log("[LinkTransactionDialog] Opened — seeding search query", {
        expenseId,
        rawTitle,
        seededTitle,
      });
      setSearchQuery(seededTitle);
      setSelected(null);
    }
  }, [open, expenseId, expense?.name]);

  useEffect(() => {
    if (!open || !expenseId) return;
    let cancelled = false;
    setLoadingSuggestions(true);
    const paymentDate = expense?.dueDate
      ? toDateInputValue(new Date(expense.dueDate))
      : undefined;
    console.log("[LinkTransactionDialog] Fetching suggestions", {
      expenseId,
      paymentDate,
      q: searchQuery,
    });
    expensePaymentAPI
      .suggestions(expenseId, {
        paymentDate,
        q: searchQuery || undefined,
        windowDays: 45,
        limit: 25,
      })
      .then((res) => {
        if (cancelled) return;
        setSuggestions(res.suggestions || []);
      })
      .catch((err) => {
        console.error("[LinkTransactionDialog] suggestions error", err);
      })
      .finally(() => {
        if (!cancelled) setLoadingSuggestions(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, expenseId, searchQuery]);

  const handleSelect = (s: PaymentSuggestion) => {
    const t = s.transaction;
    setSelected({
      id: t.transaction_id,
      label: t.merchant_name || t.name || t.transaction_id,
      amount: Math.abs(Number(t.amount || 0)),
      date: t.date,
    });
  };

  const handleConfirm = async () => {
    setSaving(true);
    try {
      console.log("[LinkTransactionDialog] Confirm link", { selected });
      await onConfirm(
        selected ? selected.id : null,
        selected ? { amount: selected.amount, date: selected.date } : undefined
      );
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;
    onCancel();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <LinkIcon />
        Link Transaction
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {expense
            ? `Link a bank transaction to "${expense.name}".`
            : "Link a bank transaction to this expense."}
        </Typography>

        {alreadyLinkedId && !selected && (
          <Chip
            icon={<LinkIcon />}
            label={`Currently linked: ${alreadyLinkedId}`}
            onDelete={() => onConfirm(null)}
            deleteIcon={<LinkOffIcon />}
            color="success"
            sx={{ mb: 2 }}
          />
        )}

        {selected ? (
          <Chip
            icon={<LinkIcon />}
            label={selected.label}
            onDelete={() => setSelected(null)}
            deleteIcon={<LinkOffIcon />}
            color="primary"
            sx={{ mb: 1 }}
          />
        ) : (
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder="Search transactions by name or merchant"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 1 }}
          />
        )}

        {!selected && (
          <Paper variant="outlined" sx={{ maxHeight: 320, overflow: "auto" }}>
            {loadingSuggestions ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <CircularProgress size={20} />
              </Box>
            ) : suggestions.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                No matching transactions found nearby.
              </Typography>
            ) : (
              <List dense disablePadding>
                {suggestions.map((s) => {
                  const t = s.transaction;
                  const amt = Math.abs(Number(t.amount || 0));
                  const label = t.merchant_name || t.name || "Unnamed";
                  const dateStr = t.date
                    ? new Date(t.date).toLocaleDateString()
                    : "";
                  const isCurrent = t.transaction_id === alreadyLinkedId;
                  return (
                    <ListItemButton
                      key={t.transaction_id}
                      selected={isCurrent}
                      onClick={() => handleSelect(s)}
                    >
                      <ListItemText
                        primary={`${label} — $${amt.toFixed(2)}${
                          isCurrent ? "  (linked)" : ""
                        }`}
                        secondary={`${dateStr} • ${s.daysOff.toFixed(
                          0
                        )}d off, $${s.amountDiff.toFixed(2)} diff`}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </Paper>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!selected || saving}
          startIcon={saving ? <CircularProgress size={16} /> : <LinkIcon />}
          onClick={handleConfirm}
          sx={{
            backgroundColor: "#805AD5",
            "&:hover": { backgroundColor: "#6B46C1" },
          }}
        >
          {saving ? "Linking..." : "Link"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LinkTransactionDialog;
